import { Animated, Dimensions, Easing, Modal, ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import BMCard from '../Card/BMCard';
import BMText from '../Text/BMText';
import { BMPrimitives } from '../../theme';
import BMButtonIcon from '../ButtonIcon/BMButtonIcon';
import IcSystemCrossClose from '@traveloka/district-icon/IcSystemCrossClose';
import BMOverlay from '../Overlay/BMOverlay';
import { DRNUtil } from '@traveloka/district-util';
const BMPopup = (props) => {
    const { children, width = 0, isVisible: visible, onPopupClose, title, withCloseButton, dismissible = true, FooterComponent, testID, disablePadding, minWidth = 640, maxWidth = 960, } = props;
    const { height: windowHeight, width: windowWidth } = useWindowDimensions();
    const handleWidth = (width) => {
        let normalizedWidth = 100;
        if (width.includes('%')) {
            normalizedWidth = Number(width.split('%')[0]);
        }
        else if (width.includes('vw')) {
            normalizedWidth = Number(width.split('vw')[0]);
        }
        return Dimensions.get('window').width * (normalizedWidth / 100);
    };
    const compWidth = useMemo(() => (width > maxWidth ? maxWidth : Math.max(typeof width === 'number' ? width : handleWidth(width), minWidth)), [width]);
    const scrollViewRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    // animations
    const cardOpacityAnimation = useRef(new Animated.Value(0)).current;
    const scaleAnimation = useRef(new Animated.Value(0)).current;
    const cardOpacityInterpolation = cardOpacityAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 1],
    });
    const scaleInterpolation = scaleAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 1],
    });
    // handlers
    const handleClose = () => {
        const { x1, y1, x2, y2 } = BMPrimitives.BMAnimation.transition.easeInOut;
        Animated.parallel([
            Animated.timing(cardOpacityAnimation, {
                toValue: 0,
                duration: BMPrimitives.BMAnimation.duration.quick,
                useNativeDriver: false,
                easing: Easing.bezier(x1, y1, x2, y2),
            }),
            Animated.timing(scaleAnimation, {
                toValue: 0,
                duration: BMPrimitives.BMAnimation.duration.quick,
                useNativeDriver: false,
                easing: Easing.bezier(x1, y1, x2, y2),
            }),
        ]).start(() => {
            setIsVisible(false);
            onPopupClose();
        });
    };
    const handleOpen = () => {
        const { x1, y1, x2, y2 } = BMPrimitives.BMAnimation.transition.easeInOut;
        const bounceEasing = BMPrimitives.BMAnimation.transition.easeOutBounce;
        setIsVisible(true);
        Animated.parallel([
            Animated.timing(cardOpacityAnimation, {
                toValue: 1,
                duration: BMPrimitives.BMAnimation.duration.moderate,
                useNativeDriver: false,
                easing: Easing.bezier(x1, y1, x2, y2),
            }),
            Animated.timing(scaleAnimation, {
                toValue: 1,
                duration: BMPrimitives.BMAnimation.duration.moderate,
                useNativeDriver: false,
                easing: Easing.bezier(bounceEasing.x1, bounceEasing.y1, bounceEasing.x2, bounceEasing.y2),
            }),
        ]).start();
    };
    useEffect(() => {
        if (visible && !isVisible) {
            handleOpen();
        }
        else if (!visible && isVisible) {
            handleClose();
        }
    }, [visible]);
    const renderFooterElement = useMemo(() => {
        var _a;
        /**
         * React.isValidElement will check if the element is valid react element/JSXElement or not
         * If it is not valid, then that means it is a ReactNode, as it is the type of the Footer Component
         * ReactNode include: string, number, Iterable<ReactNode>, ReactPortal, boolean, null, or undefined
         */
        if (!React.isValidElement(FooterComponent)) {
            return FooterComponent;
        }
        else {
            const footerStyle = ((_a = FooterComponent.props) === null || _a === void 0 ? void 0 : _a.style) || {};
            return React.cloneElement(FooterComponent, {
                // @ts-ignore
                style: [
                    {
                        padding: BMPrimitives.BMSize.xl,
                    },
                    footerStyle,
                ],
            });
        }
    }, [FooterComponent]);
    return (<Modal visible={isVisible} onRequestClose={() => dismissible && handleClose()} animationType={'none'} transparent>
      <Animated.View pointerEvents={'box-none'} style={[
            StyleSheet.absoluteFillObject,
            styles.card,
            {
                opacity: cardOpacityInterpolation,
                width: compWidth < windowWidth ? compWidth : windowWidth,
                transform: [{ scale: scaleInterpolation }],
                paddingHorizontal: BMPrimitives.BMSize.md,
            },
        ]} testID={testID}>
        <BMCard layout="SPACIOUS" elevation="FLOAT" style={{ maxHeight: windowHeight * 0.8, width: '100%' }} customStyle={{ padding: 0 }}>
          {/* header  */}
          {!!title || withCloseButton ? (<View style={[
                styles.header,
                { justifyContent: title ? 'space-between' : 'flex-end' },
                disablePadding && { paddingTop: 0, paddingHorizontal: 0 },
            ]}>
              {!!title && (<View style={styles.headerTitleContainer}>
                  <BMText variant="titleNormal" ink="BMStyleTextInkPrimary" numberOfLines={2}>
                    {title}
                  </BMText>
                </View>)}
              {withCloseButton && (<View style={styles.headerCloseBtnContainer}>
                  <BMButtonIcon onPress={handleClose} Icon={IcSystemCrossClose}/>
                </View>)}
            </View>) : null}

          {/* content  */}
          <ScrollView ref={scrollViewRef} style={[
            styles.content,
            { flexGrow: 0 },
            disablePadding && { paddingHorizontal: 0, paddingBottom: 0 },
            // @ts-ignore: for web so esc button to dismiss don't trigger focus on content (somehow it happen)
            DRNUtil.isWeb() && { outlineStyle: 'none' },
        ]}>
            {children}
          </ScrollView>
          {renderFooterElement}
        </BMCard>
      </Animated.View>

      <BMOverlay visible={isVisible} dismissible={dismissible} onDismiss={handleClose}/>
    </Modal>);
};
const styles = StyleSheet.create({
    overlay: { flex: 1 },
    card: {
        zIndex: 997,
        margin: 'auto',
        alignSelf: 'center',
        alignItems: 'center',
        justifyContent: 'center',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        paddingTop: BMPrimitives.BMSize.xl,
        paddingHorizontal: BMPrimitives.BMSize.xl,
        marginBottom: BMPrimitives.BMSize.lg,
    },
    content: {
        paddingHorizontal: BMPrimitives.BMSize.xl,
        paddingBottom: BMPrimitives.BMSize.xl,
    },
    appPressableBackground: {
        position: 'absolute',
        flex: 1,
        width: '100%',
        height: '100%',
        zIndex: -1,
    },
    headerTitleContainer: {
        flex: 1,
    },
    headerCloseBtnContainer: { marginLeft: BMPrimitives.BMSize.lg },
});
export default BMPopup;
